import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Calculator, ArrowRight, Home, Percent, Wallet, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import { buildGraph, webPageJsonLd, breadcrumbJsonLd } from "@/data/seo";

const RED = "#D41F30";

const fmt = (n) =>
  Math.round(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const calcMonthly = (principal, annualRate, years) => {
  if (principal <= 0) return 0;
  const r = annualRate / 100 / 12;
  const n = years * 12;
  if (r === 0) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

const FieldRow = ({ Icon, label, valueDisplay, children }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <span className="inline-flex items-center gap-2 text-sm md:text-base font-semibold text-gray-900">
        <Icon className="w-4 h-4" style={{ color: RED }} />
        {label}
      </span>
      <span className="text-xl md:text-2xl font-black tabular-nums" style={{ color: RED }}>
        {valueDisplay}
      </span>
    </div>
    {children}
  </div>
);

const Slider = ({ min, max, step, value, onChange, testid }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="cw-slider w-full h-2 rounded-full appearance-none cursor-pointer"
      style={{
        background: `linear-gradient(to right, ${RED} 0%, ${RED} ${pct}%, #FCE7E8 ${pct}%, #FCE7E8 100%)`,
      }}
    />
  );
};

export default function MortgageCalculatorPage() {
  const [homePrice, setHomePrice] = useState(450000);
  const [downPct, setDownPct] = useState(10);
  const [rate, setRate] = useState(6.75);
  const [term, setTerm] = useState(30);
  const [propertyTax, setPropertyTax] = useState(1.1); // % per year of home value
  const [insurance, setInsurance] = useState(1200); // annual

  const down = useMemo(() => (homePrice * downPct) / 100, [homePrice, downPct]);
  const loanAmount = homePrice - down;
  const piMonthly = useMemo(() => calcMonthly(loanAmount, rate, term), [loanAmount, rate, term]);
  const taxMonthly = (homePrice * (propertyTax / 100)) / 12;
  const insMonthly = insurance / 12;
  const pmiMonthly = downPct < 20 ? (loanAmount * 0.0055) / 12 : 0;
  const totalMonthly = piMonthly + taxMonthly + insMonthly + pmiMonthly;

  return (
    <div className="bg-white" data-testid="mortgage-calculator-page">
      <SEO
        title="Mortgage Calculator | Estimate your monthly payment"
        description="Free mortgage calculator with sliders for home price, down payment, rate, and term. Shows principal, interest, property tax, insurance, and PMI breakdown."
        path="/mortgage-calculator"
        jsonLd={buildGraph(
          webPageJsonLd({
            name: "Mortgage Calculator | CityWorth",
            description: "Calculate your monthly mortgage payment with property tax, insurance, and PMI.",
            path: "/mortgage-calculator",
          }),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Buy", path: "/buy-a-home" },
            { name: "Mortgage Calculator", path: "/mortgage-calculator" },
          ])
        )}
      />
      {/* Hero */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-16 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-red-100 rounded-full px-4 py-2 mb-6 shadow-sm">
            <Calculator className="w-4 h-4" style={{ color: RED }} />
            <span className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: RED }}>
              Mortgage Calculator
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.05] tracking-tight mb-5">
            Estimate your monthly payment.
          </h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Adjust the sliders to see how home price, down payment, and rate affect your payment. Real numbers from a CityWorth expert, even better.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            {/* Inputs */}
            <div className="bg-gray-50 rounded-3xl border border-gray-100 p-7 md:p-9 space-y-8">
              <FieldRow Icon={Home} label="Home price" valueDisplay={fmt(homePrice)}>
                <Slider min={50000} max={2000000} step={5000} value={homePrice} onChange={setHomePrice} testid="calc-home-price" />
                <div className="flex justify-between text-xs text-gray-400 mt-2">
                  <span>$50K</span><span>$2M</span>
                </div>
              </FieldRow>
              <FieldRow Icon={Wallet} label="Down payment" valueDisplay={`${downPct}% · ${fmt(down)}`}>
                <Slider min={0} max={50} step={1} value={downPct} onChange={setDownPct} testid="calc-down-pct" />
                <div className="flex justify-between text-xs text-gray-400 mt-2">
                  <span>0%</span><span>50%</span>
                </div>
              </FieldRow>
              <FieldRow Icon={Percent} label="Interest rate" valueDisplay={`${rate.toFixed(2)}%`}>
                <Slider min={2} max={12} step={0.05} value={rate} onChange={setRate} testid="calc-rate" />
                <div className="flex justify-between text-xs text-gray-400 mt-2">
                  <span>2%</span><span>12%</span>
                </div>
              </FieldRow>
              <FieldRow Icon={Clock} label="Loan term" valueDisplay={`${term} years`}>
                <div className="flex gap-2 mt-1">
                  {[15, 20, 30].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTerm(t)}
                      data-testid={`calc-term-${t}`}
                      className={`flex-1 rounded-xl py-3 text-sm font-bold transition-colors ${
                        term === t
                          ? "bg-[#D41F30] text-white"
                          : "bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {t} yrs
                    </button>
                  ))}
                </div>
              </FieldRow>
              <div className="pt-2 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Property tax (% / yr)</label>
                  <input
                    type="number"
                    step="0.05"
                    value={propertyTax}
                    onChange={(e) => setPropertyTax(Number(e.target.value || 0))}
                    className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-[#D41F30] focus:outline-none"
                    data-testid="calc-property-tax"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Home insurance ($/yr)</label>
                  <input
                    type="number"
                    step="50"
                    value={insurance}
                    onChange={(e) => setInsurance(Number(e.target.value || 0))}
                    className="w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-sm focus:border-[#D41F30] focus:outline-none"
                    data-testid="calc-insurance"
                  />
                </div>
              </div>
            </div>

            {/* Output */}
            <div>
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 md:p-10 text-white shadow-2xl">
                <p className="text-sm text-gray-400 mb-2 uppercase tracking-widest font-bold">
                  Estimated monthly payment
                </p>
                <p
                  className="font-display text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-none"
                  data-testid="calc-total-monthly"
                >
                  {fmt(totalMonthly)}
                </p>
                <div className="space-y-3 text-sm border-t border-white/10 pt-5">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Principal & Interest</span>
                    <span className="font-semibold">{fmt(piMonthly)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Property tax</span>
                    <span className="font-semibold">{fmt(taxMonthly)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Home insurance</span>
                    <span className="font-semibold">{fmt(insMonthly)}</span>
                  </div>
                  {pmiMonthly > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Mortgage insurance (PMI)</span>
                      <span className="font-semibold">{fmt(pmiMonthly)}</span>
                    </div>
                  )}
                </div>
                <div className="mt-7 pt-5 border-t border-white/10 text-xs text-gray-400 leading-relaxed">
                  Loan amount: {fmt(loanAmount)} · {term}-year fixed at {rate.toFixed(2)}%
                </div>
              </div>

              <div className="mt-6 bg-red-50 border border-red-100 rounded-2xl p-6">
                <p className="font-display text-xl font-bold text-gray-900 mb-2">
                  Want to see what your numbers actually look like?
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  This calculator gives you a ballpark. A CityWorth advisor can run real numbers for your situation in a few minutes, no pressure.
                </p>
                <Button
                  className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-6 py-3 text-sm font-semibold w-full sm:w-auto"
                  onClick={() => window.location.assign("/get-started")}
                  data-testid="calc-cta"
                >
                  Start my homebuying plan
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-8 text-center max-w-3xl mx-auto">
            This calculator is for estimation only. Actual rates and payments depend on your credit, loan program, location, and lender. PMI estimated at 0.55% annually when down payment is under 20%.
          </p>
        </div>
      </section>
    </div>
  );
}
